import { Link } from 'react-router-dom';
import { Button } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import type { BillReturn } from '@/services/billing.service';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { returnStatusMeta } from '@/lib/return-status';

export function ReturnStatusPill({ status }: { status: string }) {
  const meta = returnStatusMeta(status);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.className}`}
      data-testid="return-status"
    >
      {meta.label}
    </span>
  );
}

interface ReturnTrackingCardProps {
  returns: BillReturn[];
  currency: CurrencyCode;
  /** The bill's store zone: return times show on its clock, not this device's. */
  timeZone: string;
  /** Ids the current user may cancel (their own, or any for a manager). */
  canCancel: (r: BillReturn) => boolean;
  onCancel: (r: BillReturn) => void;
  cancellingId?: string | null;
}

/**
 * Ops simulation T03 — every return against a bill with its RINV number and
 * where it stands: waiting for a manager, completed (with its credit note),
 * rejected (with the manager's note) or cancelled.
 */
export function ReturnTrackingCard({
  returns,
  currency,
  timeZone,
  canCancel,
  onCancel,
  cancellingId,
}: ReturnTrackingCardProps) {
  if (returns.length === 0) return null;
  return (
    <div
      className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-100 dark:border-neutral-700 shadow-md overflow-hidden animate-fade-in"
      data-testid="return-tracking"
    >
      <div className="px-4 py-3 bg-gradient-to-r from-violet-600 to-pink-600 text-white">
        <h2 className="text-sm font-semibold">Returns ({returns.length})</h2>
      </div>
      <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
        {returns.map((r) => (
          <li
            key={r.id}
            className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm even:bg-neutral-50 dark:even:bg-neutral-900/40 hover:bg-brand-50 dark:hover:bg-neutral-700/50 transition-all duration-200 animate-slide-up"
          >
            <span className="font-mono font-semibold text-neutral-900 dark:text-white">{r.return_number}</span>
            <ReturnStatusPill status={r.status} />
            {r.amount !== null && r.amount !== undefined && (
              <span className="tabular-nums text-neutral-700 dark:text-neutral-300">
                {formatCurrency(Math.abs(Number(r.amount)), currency)}
              </span>
            )}
            {r.return_mode && (
              <span className="text-neutral-500 dark:text-neutral-400">
                {r.return_mode.replace('_', ' ').toLowerCase()}
              </span>
            )}
            <span className="text-xs text-neutral-400">{formatDateTime(r.created_at, timeZone)}</span>
            <span className="ml-auto flex items-center gap-2">
              {r.status === 'COMPLETED' && r.credit_note_bill_id && (
                <Link
                  to={`/pos/bills/${r.credit_note_bill_id}`}
                  className="text-brand-600 dark:text-brand-400 font-medium underline hover:no-underline"
                >
                  View credit note
                </Link>
              )}
              {r.status === 'WAITING_APPROVAL' && canCancel(r) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onCancel(r)}
                  loading={cancellingId === r.id}
                  disabled={!!cancellingId}
                >
                  Cancel request
                </Button>
              )}
            </span>
            {r.status === 'REJECTED' && r.review_note && (
              <p className="basis-full text-xs text-danger-700 dark:text-danger-300">Manager's note: {r.review_note}</p>
            )}
            {r.execution_error && r.status !== 'COMPLETED' && (
              <p className="basis-full text-xs text-danger-700 dark:text-danger-300">
                Approved but could not be processed: {r.execution_error}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
